<template>
  <div class="toolbar">
    <span
      ref="selectRef"
      class="toolbar__select"
      :class="{ 'toolbar__select--partial': isPartialSelection }"
      @change="onPageCheckboxChange"
    >
      <Checkbox
        :model-value="isAllPageCardChosen"
        :label="t('news_page_pagination.chose_all')"
      />
    </span>

    <div v-if="chosenCount > 0" class="toolbar__actions">
      <div
        v-tooltip:top="t('news_page_toolbar.tooltip.favorite')"
        class="toolbar__item"
        :class="{ 'toolbar__item--disabled': isAllCardsChosen }"
        @click="addToFavoriteCard(chosenCardList)"
      >
        <ToolbarBookmarkIcon class="toolbar__icon" />
      </div>

      <div
        v-tooltip:top="t('news_page_toolbar.tooltip.download')"
        class="toolbar__item"
        :class="{
          'toolbar__item--disabled': newsFeedStore.downloadTask.isProgress,
        }"
        @click="downloadCard(chosenCardList)"
      >
        <span
          v-if="newsFeedStore.downloadTask.isProgress"
          class="toolbar__spinner"
        />
        <ToolbarDownloadIcon v-else class="toolbar__icon" />
      </div>

      <div
        v-tooltip:top="t('news_page_toolbar.tooltip.delete')"
        class="toolbar__item"
        :class="{ 'toolbar__item--disabled': isAllCardsChosen }"
        @click="isShowDeleteCardModal = true"
      >
        <ToolbarTrashIcon class="toolbar__icon" />
      </div>

      <div class="toolbar__item toolbar__item-total" @click="resetChosenCards">
        {{ t("news_page_toolbar.tooltip.chosen") }}
        {{ formatCount(chosenCount) }}
        <IconCrossSemi class="toolbar__icon" />
      </div>
    </div>
  </div>

  <div v-if="showSelectAllBanner" class="toolbar-banner">
    <InfoIcon class="toolbar-banner__icon" />
    <p class="toolbar-banner__text">
      <b>{{ t(bannerSelectedKey) }}</b>

      <span>
        &nbsp;{{ t("news_page_toolbar.banner.select_all") }}
        {{ formatCount(newsFeedStore.totalCount) }}
        {{ bannerNewsWord }}
      </span>
    </p>
    <button type="button" class="toolbar-banner__accept" @click="selectAllNews">
      {{ t("news_page_toolbar.banner.accept") }}
    </button>
    <button
      type="button"
      class="toolbar-banner__dismiss"
      @click="closeSelectAllBanner"
    >
      {{ t("news_page_toolbar.banner.close") }}
    </button>
  </div>

  <Modal
    v-if="isShowDeleteCardModal"
    teleport="#app"
    :confirm-text="t('news_page_popup_delete.button.accept_delete')"
    :title="t('news_page_toolbar.delete_modal.title')"
    :use-cross-icon="false"
    @cancel="isShowDeleteCardModal = false"
    @confirm="deleteCard(chosenCardList)"
    @close="isShowDeleteCardModal = false"
  >
    {{ t("news_page_toolbar.delete_modal.text") }}
  </Modal>
</template>

<script setup>
import ToolbarBookmarkIcon from "@/components/Icons/ToolbarBookmarkIcon.vue";
import ToolbarDownloadIcon from "@/components/Icons/ToolbarDownloadIcon.vue";
import ToolbarTrashIcon from "@/components/Icons/ToolbarTrashIcon.vue";
import IconCrossSemi from "@/components/Icons/IconCrossSemi.vue";
import InfoIcon from "@/components/Icons/InfoIcon.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useLK } from "@/controllerLK/LKstore.js";
import { computed, defineProps, ref, defineEmits, watch, onMounted } from "vue";
import { useNotificationsStore } from "@/store/notifications";
import { t } from "@/controllerLK/GlobusLocalization";
import { getPluralForm } from "@/tools/date-time-tools";

const props = defineProps({
  chosenCardList: {
    type: Array,
    required: true,
    default: () => [],
  },
  chosenDetailCardList: {
    type: Array,
    default: () => [],
  },
  isAllCardsChosen: {
    type: Boolean,
    default: false,
  },
  isAllPageCardChosen: {
    type: Boolean,
    default: false,
  },
  chosenRubricIds: {
    type: Array,
    default: () => [],
  },
  excludedCardsIds: {
    type: Array,
    default: () => [],
  },
  excludedDetailCardList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "reset-card-list",
  "toggle-all-cards",
  "select-all-cards",
]);

// Сторы
const newsFeedStore = useNewsFeedStore();
const notificationStore = useNotificationsStore();

const chosenCount = computed(() =>
  props.isAllCardsChosen
    ? newsFeedStore.totalCount - props.excludedCardsIds.length
    : props.chosenCardList.length
);

const pageCardIdList = computed(() =>
  (newsFeedStore.items || []).map((item) => item.id)
);

// Часть карточек текущей страницы выбрана, но не все карточки этой страницы.
const isPartialSelection = computed(() => {
  if (!pageCardIdList.value.length || props.isAllPageCardChosen) return false;

  const selectedIdSet = new Set(props.chosenCardList);
  return pageCardIdList.value.some((id) => selectedIdSet.has(id));
});

const selectRef = ref(null);

const syncPartialCheckbox = () => {
  const input = selectRef.value?.querySelector("input[type='checkbox']");
  if (input) input.indeterminate = isPartialSelection.value;
};

watch(isPartialSelection, syncPartialCheckbox, { flush: "post" });
onMounted(syncPartialCheckbox);

const pageSize = computed(() => Number(newsFeedStore.take) || 0);

// Все новости текущей страницы выбраны, и в ленте есть ещё новости.
const isCurrentPageFullySelected = computed(
  () =>
    props.isAllPageCardChosen &&
    pageCardIdList.value.length > 0 &&
    newsFeedStore.totalCount > pageCardIdList.value.length
);

// Больше одной страницы или вся текущая страница, но ещё не весь список.
const isOverPageSelection = computed(
  () =>
    pageSize.value > 0 &&
    chosenCount.value < newsFeedStore.totalCount &&
    (chosenCount.value > pageSize.value || isCurrentPageFullySelected.value)
);

// Показанная плашка остаётся при смене страницы, пока выбор не сброшен
// и выбраны не все новости.
const isBannerKept = computed(
  () =>
    newsFeedStore.selectAllBannerClosedState === 1 &&
    chosenCount.value > 0 &&
    chosenCount.value < newsFeedStore.totalCount
);

const syncSelectAllBannerState = () => {
  // 2 остаётся, пока действие в сторе не вернёт плашку в 0.
  if (newsFeedStore.selectAllBannerClosedState === 2) return;
  if (isBannerKept.value) return;
  newsFeedStore.selectAllBannerClosedState = isOverPageSelection.value ? 1 : 0;
};

const showSelectAllBanner = computed(
  () => newsFeedStore.selectAllBannerClosedState === 1
);

const isMultiPageSelection = computed(
  () => pageSize.value > 0 && chosenCount.value > pageSize.value
);

const bannerSelectedKey = computed(() =>
  isMultiPageSelection.value
    ? "news_page_toolbar.banner.page_selected"
    : "news_page_toolbar.banner.single_page_selected"
);

const bannerNewsWord = computed(() =>
  getPluralForm(
    Number(newsFeedStore.totalCount) || 0,
    t("news_page_toolbar.banner.news.one"),
    t("news_page_toolbar.banner.news.few"),
    t("news_page_toolbar.banner.news.many")
  )
);

watch(isOverPageSelection, syncSelectAllBannerState, { immediate: true });

// Любой выбор карточки заново проверяет условие показа.
watch(chosenCount, syncSelectAllBannerState);

// getRubricNews (и пагинация через getNews) заменяет items, totalCount и take.
watch(
  () => [newsFeedStore.items, newsFeedStore.totalCount, newsFeedStore.take],
  syncSelectAllBannerState
);

const formatCount = (value) =>
  String(value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const onPageCheckboxChange = (event) => {
  if (typeof event.target?.checked !== "boolean") return;
  emit("toggle-all-cards", event.target.checked);
};

const resetChosenCards = () => {
  emit("reset-card-list");
};

const selectAllNews = () => {
  emit("select-all-cards");
  newsFeedStore.selectAllBannerClosedState = 0;
};

const closeSelectAllBanner = () => {
  newsFeedStore.selectAllBannerClosedState = 2;
};

// Отображение окна подтверждения удаления карточек
const isShowDeleteCardModal = ref(false);

// Добавить карточки в избранное
const addToFavoriteCard = async (list) => {
  if (list.length === 0) return;
  newsFeedStore.loading = true;

  for (const item in list) {
    await newsFeedStore.setFavorite(list[item], true, true);
  }

  newsFeedStore.loading = false;
  notificationStore.createNotify(
    `${t("news_page_toolbar.toast.favorite")}`,
    "success"
  );
};

// Удаление карточек.
const deleteCard = async (list) => {
  if (list.length === 0) return;
  newsFeedStore.loading = true;
  isShowDeleteCardModal.value = false;

  emit("reset-card-list");

  for (const item of list) await newsFeedStore.deleteCard(item);

  newsFeedStore.loading = false;
  await new Promise((res) => setTimeout(res, 1500)).then(() =>
    newsFeedStore.getNews()
  );
  notificationStore.createNotify(
    `${t("news_page_toolbar.toast.delete")}`,
    "success"
  );
};

const toIsoDate = (unixSeconds) => new Date(unixSeconds * 1000).toISOString();

// Те же фильтры, что уходят в запрос ленты, в формате search_request выгрузки.
const buildSearchRequest = () => ({
  q: newsFeedStore.searchValue,
  mode: "by_rubric",
  date_from: toIsoDate(newsFeedStore.dateFrom),
  date_to: toIsoDate(newsFeedStore.dateTo),
  project_id: useLK().project.id,
  rubric_ids: [...props.chosenRubricIds],
  filters: {
    source_ids: [],
    source_tag_ids: [...newsFeedStore.filterTags.listID],
    city_ids: [...newsFeedStore.filterRegion.filterList],
    category_ids: [...newsFeedStore.filterCategories.filterList],
    importance: [...newsFeedStore.filterImportance.list],
    sentiments: [...newsFeedStore.filterSentiment.list],
    persons: [...newsFeedStore.filterObject.listID.persons],
    locations: [...newsFeedStore.filterObject.listID.locations],
    organisations: [...newsFeedStore.filterObject.listID.organisations],
    and_for_cities: newsFeedStore.filterRegion.condition,
    and_for_tags: newsFeedStore.filterTags.condition,
    and_for_objects: newsFeedStore.filterObject.condition,
  },
  sort_by: "relevance",
  sort_dir: "asc",
});

const hasProjectId = (projectId) =>
  projectId !== undefined && projectId !== null && projectId !== "";

// Карточка попадает либо в external_ids, либо в cards_ids.
// С пустым project_id и заполненным external_id — только external_ids.
// Иначе — только cards_ids.
const splitCardsByProject = (cards) => {
  const cardsIds = [];
  const externalIds = [];

  for (const card of cards) {
    if (!hasProjectId(card.project_id) && card.external_id) {
      externalIds.push(card.external_id);
      continue;
    }

    cardsIds.push(card.id);
  }

  return { cardsIds, externalIds };
};

const splitSelectedCards = (selectedIds) => {
  const selectedIdSet = new Set(selectedIds);

  return splitCardsByProject(
    props.chosenDetailCardList.filter((card) => selectedIdSet.has(card.id))
  );
};

// Снятая карточка с project_id уходит в excluded_cards_ids.
// С пустым project_id — её external_id в excluded_external_ids.
const splitExcludedCards = (excludedIds) => {
  const excludedIdSet = new Set(excludedIds);
  const excludedCardsIds = [];
  const excludedExternalIds = [];

  for (const card of props.excludedDetailCardList) {
    if (!excludedIdSet.has(card.id)) continue;

    if (!hasProjectId(card.project_id) && card.external_id) {
      excludedExternalIds.push(card.external_id);
      continue;
    }

    if (hasProjectId(card.project_id)) {
      excludedCardsIds.push(card.id);
    }
  }

  return { excludedCardsIds, excludedExternalIds };
};

// Скачать карточки
const downloadCard = async (list) => {
  if (newsFeedStore.downloadTask.isProgress) return;

  let cardsIds = [];
  let externalIds = [];
  let excludedCardsIds = [];
  let excludedExternalIds = [];
  let sourceType = "selected";
  let searchRequest = { mode: "by_rubric", additionalProp1: {} };

  if (props.isAllCardsChosen) {
    sourceType = "search_request";
    searchRequest = buildSearchRequest();

    ({ excludedCardsIds, excludedExternalIds } = splitExcludedCards(
      props.excludedCardsIds
    ));
  } else if (list.length === 0) {
    ({ cardsIds, externalIds } = splitCardsByProject(
      newsFeedStore.items || []
    ));
  } else {
    ({ cardsIds, externalIds } = splitSelectedCards(list));
  }

  await newsFeedStore.filesDownload(
    cardsIds,
    externalIds,
    sourceType,
    searchRequest,
    excludedCardsIds,
    excludedExternalIds,
    chosenCount.value
  );
};
</script>

<style scoped lang="scss">
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  padding: 0px 30px 0px 20px;
  color: #7b87a5;

  &__select {
    display: flex;
    align-items: center;

    :deep(.checkbox__label) {
      color: #7b87a5 !important;
      font-size: 14px;
      font-weight: 400;
    }

    &--partial {
      :deep(.checkbox__icon) {
        display: none;
      }

      :deep(.checkbox__input),
      :deep(.checkbox__input:checked) {
        border-color: transparent;
        background: #1f9aff;
      }

      :deep(.checkbox__box)::after {
        content: "";
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 50%;
        width: 8px;
        height: 2px;
        border-radius: 1px;
        background: #fff;
        transform: translate(-50%, -50%);
        pointer-events: none;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
  }

  &__item {
    font-size: 14px;
    font-weight: 400;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    color: #7b87a5;
    gap: 6px;

    width: 26px;
    height: 26px;
    padding: 2px;
    border-radius: 100px;

    &:hover {
      background: #f7f8fd;
    }

    &:active {
      background: #f7f8fd;
      outline: 1px solid #1658bc;
      outline-offset: -1px;
    }

    &-total {
      gap: 5px;
      margin-left: 10px;
      width: auto;
      padding: 5px 10px;
    }

    &--disabled {
      cursor: default;
      pointer-events: none;
      opacity: 0.45;
    }
  }

  &__icon {
    width: 17px;
    height: 17px;
    color: #7b87a5;

    :deep(path) {
      stroke: currentColor;
      stroke-width: 2;
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border: 2px solid currentColor;
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: toolbar-spin 1s linear infinite;
  }
}

.toolbar-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 12px 8px 0px;
  padding: 12px 10px;
  border-radius: 8px;
  background: #fff;

  &__icon {
    flex: 0 0 auto;
    color: #1f9aff;
    width: 20px;
    height: 20px;

    :deep(path) {
      stroke: currentColor;
    }
  }

  &__text {
    flex: 1;
    margin: 0;
    color: #2c3345;
    font-size: 14px;
    line-height: 1.35;
    font-weight: 400;

    b {
      font-weight: 500;
    }
  }

  &__accept,
  &__dismiss {
    flex: 0 0 auto;
    height: 28px;
    padding: 0 16px;
    border: 0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  &__accept {
    color: #fff;
    background: #1a64d4;
  }

  &__dismiss {
    color: #1a64d4;
    background: #f7f8fd;
  }
}

@keyframes toolbar-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
